import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import { BrandingPalette } from './BrandingPalette';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQueryClient } from '@tanstack/react-query';
import { usePageTitle } from '../../components/shell/AppShell';
import {
  useMe,
  useMyPermissions,
  useOperatorBranding,
  useOperatorLogoUrl,
  useUpdateOperatorBranding,
} from '../../api/queries';
import { useAccessToken } from '../../auth/useAccessToken';
import { apiBaseUrl } from '../../auth/config';

const MAX_LOGO_BYTES = 256 * 1024;
const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/svg+xml'];

export default function OperatorBrandingPage() {
  usePageTitle('Operator branding');
  const me = useMe();
  const perms = useMyPermissions();
  const operatorId = me.data?.operator_id ?? null;

  if (!perms.data?.codes.includes('operators.branding.edit')) {
    return (
      <>
        <Alert severity="warning">
          You don't have permission to edit operator branding.
        </Alert>
      </>
    );
  }

  if (!operatorId) {
    return (
      <>
        <Alert severity="info">
          You're signed in as ACES. To edit operator-tier branding, switch to an
          operator context — that lands in S1.6 (impersonation).
        </Alert>
      </>
    );
  }

  return <Editor operatorId={operatorId} />;
}

function Editor({ operatorId }: { operatorId: string }) {
  const branding = useOperatorBranding(operatorId);
  const update = useUpdateOperatorBranding(operatorId);
  const qc = useQueryClient();
  const getToken = useAccessToken();
  const fileInput = useRef<HTMLInputElement>(null);

  const logoUrl = useOperatorLogoUrl(operatorId, branding.data?.has_logo);

  const [primary, setPrimary] = useState<string>('');
  const [accent, setAccent]   = useState<string>('');
  const [snack, setSnack]     = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoBusy, setLogoBusy]   = useState(false);

  useEffect(() => {
    setPrimary(branding.data?.primary_hex ?? '');
    setAccent(branding.data?.accent_hex ?? '');
  }, [branding.data]);

  const dirty =
    (primary || '') !== (branding.data?.primary_hex ?? '') ||
    (accent  || '') !== (branding.data?.accent_hex ?? '');

  const validHex = (s: string) => s === '' || /^#[0-9a-fA-F]{6}$/.test(s);
  const primaryValid = validHex(primary);
  const accentValid  = validHex(accent);

  const save = () => {
    update.mutate(
      {
        primaryHex: primary || null,
        accentHex:  accent  || null,
      },
      { onSuccess: () => setSnack('Branding saved.') },
    );
  };

  const uploadLogo = async (file: File) => {
    setLogoError(null);
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(`Logo too large (max ${MAX_LOGO_BYTES / 1024} KB).`);
      return;
    }
    if (!ALLOWED_MIMES.includes(file.type)) {
      setLogoError('Logo must be PNG, JPEG, or SVG.');
      return;
    }
    setLogoBusy(true);
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${apiBaseUrl}/api/v1/operators/${operatorId}/logo`, {
        method: 'PUT',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `${res.status} ${res.statusText}`);
      }
      qc.invalidateQueries({ queryKey: ['operators', operatorId, 'branding'] });
      // Also refetch the authenticated logo blob so the new image shows
      // immediately (the <img> reads an object-URL, not a cache-busted URL).
      qc.invalidateQueries({ queryKey: ['operators', operatorId, 'logo-blob'] });
      setSnack('Logo uploaded.');
    } catch (err) {
      setLogoError((err as Error).message);
    } finally {
      setLogoBusy(false);
    }
  };

  const deleteLogo = async () => {
    if (!confirm('Remove the operator logo?')) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBaseUrl}/api/v1/operators/${operatorId}/logo`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      qc.invalidateQueries({ queryKey: ['operators', operatorId, 'branding'] });
      qc.invalidateQueries({ queryKey: ['operators', operatorId, 'logo-blob'] });
      setSnack('Logo removed.');
    } catch (err) {
      setLogoError((err as Error).message);
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <>
      <Stack spacing={3} sx={{ maxWidth: 720 }}>
        <Box>
          <Typography variant="h4">Operator branding</Typography>
          <Typography variant="body2" color="text.secondary">
            These overrides apply to every user in your operator. Per-user
            accent over-overrides live in <i>Appearance</i>.
          </Typography>
        </Box>

        {branding.isLoading ? (
          <Stack direction="row" spacing={2}><CircularProgress size={20} /><Typography>Loading…</Typography></Stack>
        ) : (
          <>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Brand colours</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pick from the preset palette, or use the pencil icon to
                  enter a custom hex. Empty = ARMS defaults.
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mb: 2 }}>
                  <BrandingPalette
                    label="Primary colour"
                    value={primary}
                    onChange={setPrimary}
                    disabled={update.isPending}
                  />
                  <BrandingPalette
                    label="Accent colour"
                    value={accent}
                    onChange={setAccent}
                    disabled={update.isPending}
                  />
                </Stack>

                {/* Live preview — same MUI primary / accent surfaces the
                    user will see on every page once saved. */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 90 }}>Preview</Typography>
                  <Box sx={{
                    height: 32, px: 2, display: 'flex', alignItems: 'center',
                    bgcolor: primary || '#1f3b6f', color: 'white',
                    borderRadius: 1, fontSize: 13, fontWeight: 600,
                  }}>
                    Primary button
                  </Box>
                  <Box sx={{
                    height: 32, px: 2, display: 'flex', alignItems: 'center',
                    bgcolor: accent || '#7d4f8f', color: 'white',
                    borderRadius: 1, fontSize: 13, fontWeight: 600,
                  }}>
                    Accent chip
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  {update.error && (
                    <ErrorAlert sx={{ flex: 1, mr: 2 }}>
                      {(update.error as { message?: string }).message ?? 'Failed to save.'}
                    </ErrorAlert>
                  )}
                  <Button
                    variant="contained"
                    onClick={save}
                    disabled={!dirty || !primaryValid || !accentValid || update.isPending}
                  >
                    {update.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Logo</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  PNG, JPEG, or SVG. Up to {MAX_LOGO_BYTES / 1024} KB. Shown
                  in the topbar of every user in your operator.
                </Typography>

                <Stack direction="row" spacing={3} alignItems="center">
                  <Box sx={{
                    width: 200, height: 80,
                    bgcolor: 'action.hover',
                    border: '1px dashed', borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    {branding.data?.has_logo && logoUrl ? (
                      <Box
                        component="img"
                        src={logoUrl}
                        alt="Operator logo"
                        sx={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No logo set
                      </Typography>
                    )}
                  </Box>
                  <Stack spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInput.current?.click()}
                      disabled={logoBusy}
                    >
                      Upload logo
                    </Button>
                    {branding.data?.has_logo && (
                      <Button
                        variant="text"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={deleteLogo}
                        disabled={logoBusy}
                      >
                        Remove logo
                      </Button>
                    )}
                    <input
                      ref={fileInput}
                      type="file"
                      hidden
                      accept="image/png,image/jpeg,image/svg+xml"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadLogo(f);
                        e.target.value = '';
                      }}
                    />
                  </Stack>
                </Stack>
                {logoError && <ErrorAlert sx={{ mt: 2 }}>{logoError}</ErrorAlert>}
              </CardContent>
            </Card>
          </>
        )}
      </Stack>

      <Snackbar
        open={snack !== null}
        autoHideDuration={2000}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </>
  );
}

// Swatch component removed — superseded by BrandingPalette.
